<template>
  <div class="ai-report-sms-summary">
    <!-- 一、数据概览 -->
    <div class="sms-section">
      <div class="sms-section-title">{{ sectionTitles.dataOverview }}</div>

      <!-- 营销数据表 -->
      <div class="sms-sub-section">
        <div class="sms-sub-title">营销数据</div>
        <jd-table
          :table-info="marketingColumns"
          :table-data="marketingTableData"
          :max-height="'320'"
          :show-page="false"
          :show-overflow-tooltip="true"
        />
      </div>

      <!-- 商品数据表 -->
      <div class="sms-sub-section">
        <div class="sms-sub-title">商品数据</div>
        <jd-table
          :table-info="productTableColumns"
          :table-data="productRows"
          :max-height="'320'"
          :show-page="false"
          :show-overflow-tooltip="true"
        />
      </div>
    </div>

    <!-- 二、核心总览 -->
    <div class="sms-section">
      <div class="sms-section-title">{{ sectionTitles.coreOverview }}</div>
      <div v-if="coreOverview.content" class="ai-report-text global-ai-content">
        <div
          v-for="(block, idx) in renderedBlocks(coreOverview.content)"
          :key="idx"
        >
          <div
            v-if="block.type === 'markdown'"
            class="md-content"
            v-html="block.html"
          ></div>
          <div v-else-if="block.type === 'table'" class="table-block">
            <md-table :html="block.html">
              <div v-html="block.html"></div>
            </md-table>
          </div>
        </div>
      </div>
    </div>

    <!-- 三、人群维度解读 -->
    <div class="sms-section">
      <div class="sms-section-title">{{ sectionTitles.audienceDimension }}</div>

      <div class="sms-chart-wrapper" v-if="hasAudienceChartData">
        <div ref="audienceChartRef" class="sms-chart"></div>
      </div>

      <div
        v-if="audienceDimension.content"
        class="ai-report-text global-ai-content"
      >
        <div
          v-for="(block, idx) in renderedBlocks(audienceDimension.content)"
          :key="idx"
        >
          <div
            v-if="block.type === 'markdown'"
            class="md-content"
            v-html="block.html"
          ></div>
          <div v-else-if="block.type === 'table'" class="table-block">
            <md-table :html="block.html">
              <div v-html="block.html"></div>
            </md-table>
          </div>
        </div>
      </div>
    </div>

    <!-- 四、货品维度解读 -->
    <div class="sms-section">
      <div class="sms-section-title">{{ sectionTitles.goodsDimension }}</div>

      <div class="sms-chart-wrapper" v-if="hasGoodsChartData">
        <div ref="goodsChartRef" class="sms-chart"></div>
      </div>

      <div
        v-if="goodsDimension.content"
        class="ai-report-text global-ai-content"
      >
        <div
          v-for="(block, idx) in renderedBlocks(goodsDimension.content)"
          :key="idx"
        >
          <div
            v-if="block.type === 'markdown'"
            class="md-content"
            v-html="block.html"
          ></div>
          <div v-else-if="block.type === 'table'" class="table-block">
            <md-table :html="block.html">
              <div v-html="block.html"></div>
            </md-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import echarts from "echarts";
import mdTable from "@/components/ai/components/mdTable.vue";

export default {
  name: "AiReportSmsSummary",
  components: { mdTable },
  props: {
    summary: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      audienceChartInstance: null,
      goodsChartInstance: null,
    };
  },
  computed: {
    sectionTitles() {
      return (
        this.summary.sectionTitles || {
          dataOverview: "一、数据总览",
          coreOverview: "二、核心总览",
          audienceDimension: "三、人群维度解读",
          goodsDimension: "四、货品维度解读",
        }
      );
    },
    coreOverview() {
      return this.summary.coreOverview || {};
    },
    audienceDimension() {
      return this.summary.audienceDimension || {};
    },
    goodsDimension() {
      return this.summary.goodsDimension || {};
    },
    metricMetas() {
      return this.summary.metricMetas || [];
    },
    batchRows() {
      return this.summary.batchRows || [];
    },
    totalRow() {
      return this.summary.totalRow || null;
    },
    productRows() {
      return this.summary.productRows || [];
    },
    productColumns() {
      return this.summary.productColumns || [];
    },
    charts() {
      return this.summary.charts || [];
    },
    audienceChart() {
      return this.charts[0] || null;
    },
    goodsChart() {
      return this.charts[1] || null;
    },
    hasAudienceChartData() {
      if (!this.audienceChart) return false;
      const barVals =
        (this.audienceChart.barSeries && this.audienceChart.barSeries.values) ||
        [];
      const lineVals =
        (this.audienceChart.lineSeries &&
          this.audienceChart.lineSeries.values) ||
        [];
      return (
        this.audienceChart.categories &&
        this.audienceChart.categories.length > 0 &&
        (barVals.length > 0 || lineVals.length > 0)
      );
    },
    hasGoodsChartData() {
      if (!this.goodsChart) return false;
      const barVals =
        (this.goodsChart.barSeries && this.goodsChart.barSeries.values) || [];
      const lineVals =
        (this.goodsChart.lineSeries && this.goodsChart.lineSeries.values) || [];
      return (
        this.goodsChart.categories &&
        this.goodsChart.categories.length > 0 &&
        (barVals.length > 0 || lineVals.length > 0)
      );
    },
    // 营销数据表列配置
    marketingColumns() {
      return this.metricMetas.map((meta) => ({
        prop: meta.key,
        label: meta.label,
        minWidth: meta.category === "DIM" ? 150 : 110,
        showOverflowTooltip: true,
      }));
    },
    // 营销数据表数据（末尾追加合计行）
    marketingTableData() {
      const rows = this.batchRows.map((row) => ({ ...row.metrics }));
      if (this.totalRow) {
        rows.unshift({ ...(this.totalRow.metrics || {}) });
      }
      return rows;
    },
    // 商品数据表列配置
    productTableColumns() {
      return this.productColumns.map((col) => ({
        prop: col.key,
        label: col.label,
        minWidth: col.category === "DIM" ? 150 : 110,
        showOverflowTooltip: true,
      }));
    },
    renderedBlocks() {
      return (text) => {
        return this.$parseMarkdownToBlocks(text);
      };
    },
  },
  watch: {
    summary: {
      handler() {
        this.$nextTick(() => {
          this.disposeCharts();
          this.initAudienceChart();
          this.initGoodsChart();
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initAudienceChart();
      this.initGoodsChart();
      window.addEventListener("resize", this.handleResize);
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.disposeCharts();
  },
  methods: {
    disposeCharts() {
      if (this.audienceChartInstance) {
        this.audienceChartInstance.dispose();
        this.audienceChartInstance = null;
      }
      if (this.goodsChartInstance) {
        this.goodsChartInstance.dispose();
        this.goodsChartInstance = null;
      }
    },
    initAudienceChart() {
      if (!this.audienceChart || !this.$refs.audienceChartRef) return;
      if (!this.hasAudienceChartData) return;
      this.audienceChartInstance = echarts.init(this.$refs.audienceChartRef);
      this.audienceChartInstance.setOption(
        this.buildDualAxisOption(this.audienceChart)
      );
    },
    initGoodsChart() {
      if (!this.goodsChart || !this.$refs.goodsChartRef) return;
      if (!this.hasGoodsChartData) return;
      this.goodsChartInstance = echarts.init(this.$refs.goodsChartRef);
      this.goodsChartInstance.setOption(
        this.buildDualAxisOption(this.goodsChart)
      );
    },
    buildDualAxisOption(chart) {
      const barName = (chart.barSeries && chart.barSeries.name) || "";
      const lineName = (chart.lineSeries && chart.lineSeries.name) || "";
      const barValues = (chart.barSeries && chart.barSeries.values) || [];
      const lineValues = (chart.lineSeries && chart.lineSeries.values) || [];
      const categories = chart.categories || [];

      return {
        title: {
          // text: chart.title || "",
          left: "center",
          textStyle: { fontSize: 14, fontWeight: "bold", color: "#333" },
        },
        tooltip: {
          trigger: "axis",
        },
        legend: {
          data: [
            barName ? { name: barName, icon: "circle" } : null,
            lineName || null,
          ].filter(Boolean),
          bottom: 0,
          textStyle: { color: "#4E5969" },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "12%",
          top: "5%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: categories,
          axisLine: { lineStyle: { color: "#E5E6EB" } },
          axisLabel: {
            interval: function (index) {
              const total = categories.length;
              if (total <= 12) return true;
              const step = Math.ceil(total / 12);
              return index % step === 0;
            },
            color: "#86909C",
            formatter: function (value) {
              if (!value) return "";
              const str = String(value);
              return str.length > 4 ? str.slice(0, 4) + "..." : str;
            },
          },
          axisTick: { show: false },
        },
        yAxis: [
          {
            type: "value",
            position: "left",
            axisLine: { show: false },
            axisLabel: {
              color: "#4E5969",
            },
            axisTick: { show: false },
            splitLine: {
              lineStyle: { color: "#F2F3F5" },
            },
          },
          {
            type: "value",
            position: "right",
            axisLine: { show: false },
            axisLabel: {
              color: "#4E5969",
            },
            axisTick: { show: false },
            splitLine: { show: false },
          },
        ],
        series: [
          {
            name: barName,
            type: "bar",
            data: barValues,
            itemStyle: { color: "#246EFF" },
            barWidth: 20,
          },
          {
            name: lineName,
            type: "line",
            yAxisIndex: 1,
            data: lineValues,
            itemStyle: { color: "#1EE7FF" },
            lineStyle: { color: "#1EE7FF", width: 2 },
            symbol: "emptyCircle",
            symbolSize: 6,
            smooth: true,
          },
        ],
      };
    },
    handleResize() {
      if (this.audienceChartInstance) this.audienceChartInstance.resize();
      if (this.goodsChartInstance) this.goodsChartInstance.resize();
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-report-sms-summary {
  width: 100%;
}

.sms-section {
  margin-bottom: 28px;
}

.sms-section-title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
}

.sms-sub-section {
  margin-bottom: 20px;
}

.sms-sub-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.5;
}

.sms-chart-wrapper {
  position: relative;
  width: 100%;
  height: 320px;
  margin-bottom: 16px;
}

.sms-chart {
  width: 100%;
  height: 100%;
}

.sms-chart-empty {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
  background: #fafafa;
}

::v-deep .global-ai-content {
  font-size: 14px;
  color: #333;
  line-height: 1.8;

  .md-content {
    margin-bottom: 10px;

    p {
      margin: 6px 0;
    }

    ul,
    ol {
      padding-left: 20px;
      margin: 6px 0;
    }

    li {
      margin: 4px 0;
    }

    strong {
      font-weight: bold;
    }
  }
}
</style>
